const LANGUAGE_CHAR_MAPS: Record<string, Record<string, string>> = {
  de: {
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    ß: 'ss',
  },
  es: {
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    ü: 'u',
    ñ: 'n',
  },
  fr: {
    à: 'a',
    â: 'a',
    ä: 'a',
    é: 'e',
    è: 'e',
    ê: 'e',
    ë: 'e',
    î: 'i',
    ï: 'i',
    ô: 'o',
    ö: 'o',
    ù: 'u',
    û: 'u',
    ü: 'u',
    ÿ: 'y',
    ç: 'c',
  },
  it: {
    à: 'a',
    è: 'e',
    é: 'e',
    ì: 'i',
    ò: 'o',
    ù: 'u',
  },
  pt: {
    á: 'a',
    â: 'a',
    ã: 'a',
    à: 'a',
    é: 'e',
    ê: 'e',
    í: 'i',
    ó: 'o',
    ô: 'o',
    õ: 'o',
    ú: 'u',
    ü: 'u',
    ç: 'c',
  },
};

function applyLanguageConversions(text: string, locale: string): string {
  const languageCode = locale.split('-')[0] ?? 'en';
  const charMap = LANGUAGE_CHAR_MAPS[languageCode];

  if (!charMap) {
    return text;
  }

  let result = text;
  for (const [char, replacement] of Object.entries(charMap)) {
    const escapedChar = char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escapedChar, 'g'), replacement);
  }

  return result;
}

function normalizeToAscii(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function generateSlug(title: string, locale = 'en'): string {
  if (!title || typeof title !== 'string') {
    return '';
  }

  let slug = title.toLowerCase();
  slug = applyLanguageConversions(slug, locale);
  slug = normalizeToAscii(slug);
  slug = slug.replace(/[^a-z0-9\s-]/g, '');
  slug = slug.replace(/\s+/g, '-');
  slug = slug.replace(/-+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');

  return slug;
}

export function generateStorySlug(title: string, id: number, locale = 'en', urlSlug?: string | null): string {
  if (urlSlug) {
    return `${urlSlug}-${id}`;
  }

  const slug = generateSlug(title, locale);

  if (!slug) {
    return String(id);
  }

  return `${slug}-${id}`;
}

export function generateCategorySlug(title: string, id: number, locale = 'en'): string {
  const slug = generateSlug(title, locale);

  if (!slug) {
    return String(id);
  }

  return `${slug}-${id}`;
}

export function generateListSlug(name: string, id: number, locale = 'en'): string {
  const slug = generateSlug(name, locale);

  if (!slug) {
    return String(id);
  }

  return `${slug}-${id}`;
}

export function parseIdFromSlug(slugParam: string): number | null {
  if (!slugParam || typeof slugParam !== 'string') {
    return null;
  }

  if (/^\d+$/.test(slugParam)) {
    return parseInt(slugParam, 10);
  }

  const lastHyphenIndex = slugParam.lastIndexOf('-');

  if (lastHyphenIndex === -1) {
    return null;
  }

  const idSegment = slugParam.substring(lastHyphenIndex + 1);

  if (!/^\d+$/.test(idSegment)) {
    return null;
  }

  return parseInt(idSegment, 10);
}

export function isLegacyNumericFormat(slugParam: string): boolean {
  return /^\d+$/.test(slugParam);
}

export function isValidSlugFormat(slugParam: string): boolean {
  if (!slugParam || typeof slugParam !== 'string') {
    return false;
  }

  if (isLegacyNumericFormat(slugParam)) {
    return true;
  }

  return /-\d+$/.test(slugParam);
}

export function getCorrectSlugIfDifferent(
  currentSlug: string,
  title: string,
  id: number,
  locale = 'en',
): string | null {
  if (isLegacyNumericFormat(currentSlug)) {
    return null;
  }

  const correctSlug = generateStorySlug(title, id, locale);

  if (currentSlug !== correctSlug) {
    return correctSlug;
  }

  return null;
}
